// Redux Utils
import { useSelector, useDispatch } from "react-redux";

// React Utils
import React, { useState, useEffect, useCallback, useRef } from "react";

// Packages Components
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Componentes
import Modal from "@/components/Modals/Modal";
import AuthPromptModal from "@/components/Modals/AuthPromptModal";
import ActualizarPerfilYPostularATicketGratis from "@/components/Forms/ActualizarPerfilYPostularATicketGratis";
import PoliticasYPostularATicketGratis from "@/components/Forms/PoliticasYPostularATicketGratis";
import Tooltip from "@/components/Tooltip";

// Contextos
import { useCart } from "@/contexts/CartContext";

// Thunks
import {
  crearCarrito,
  addTicketsFromEventToExistingCart,
} from "@/features/carrito/carritoThunks";
import { crearSolicitudInvitacion } from "@/features/invitacion/invitacionThunks";
import { updateRedesPerfil, getUserInfo } from "@/features/user/userThunks";

// Utils
import { getColorByEstadoInvitacion } from "@/utils/invitacionUtils";
import { validarRUT, formatearRUT } from "@/utils/rutValidator";

// Router
import { Link, useNavigate } from "react-router-dom";

// Referral utilities
import { getReferralForEvent } from "@/utils/referralLinkManager";

const tieneDocumento = (u) => {
  const tipo = u?.tipoDocumento || 'rut';
  return tipo === 'pasaporte' ? !!u?.numeroPasaporte : !!u?.numeroTributario;
};

const urlToHandle = (url, domain) => {
  if (!url) return "";
  if (!url.includes(domain)) return url.startsWith("@") ? url : `@${url}`;
  const match = url.match(new RegExp(`${domain}/[@]?([a-zA-Z0-9._-]+)`));
  return match ? `@${match[1]}` : "";
};
const handleToURL = (handle, base, includeDogSign = false) => {
  const clean = handle.trim().replace(/^@/, "");
  return `${base}${includeDogSign ? "@" : ""}${clean}`;
};
const validateSocialHandle = (value) => {
  if (!value || value.trim() === "" || value.trim() === "@") return false;
  return /^@?[a-zA-Z0-9._-]{1,50}$/.test(value.trim());
};

export default function CardTickets({ evento, LogoPerfilEmpresa }) {
  // Redux State and Dispatch
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { miSolicitudEnEvento } = useSelector((state) => state.invitacion);
  const { info } = useSelector((state) => state.user);

  // Ref so useCallback closures always read the latest user without needing it as a dep
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const infoRef = useRef(info);
  useEffect(() => { infoRef.current = info; }, [info]);

  // Pre-fetch user info so social networks are available for the perfil modal
  useEffect(() => {
      console.log("getUserInfo - ", "user?.uid", user?.uid, " info", info)
    if (isAuthenticated && user?.uid && !info) {
      // console.log("getUserInfo ", getUserInfo({ uid: user.uid }))
      dispatch(getUserInfo({ uid: user.uid }));
    }
  }, [isAuthenticated, user?.uid]);

  // Per-ticket invitation state: { [ticketId]: { loading, error, solicitudCreada } }
  const [invitacionState, setInvitacionState] = useState({});

  // Modal States
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const [
    openModalActualizarYPostularATicketGratis,
    setOpenModalActualizarYPostularATicketGratis,
  ] = useState(false);
  const [openModalPoliticasYPostular, setOpenModalPoliticasYPostular] =
    useState(false);
  const [avisoSolicitudEnviada, setAvisoSolicitudEnviada] = useState(false);
  const [openModalErrorReferido, setOpenModalErrorReferido] = useState(false);
  const [errorReferidoMessage, setErrorReferidoMessage] = useState("");
  const [showPerfilModal, setShowPerfilModal] = useState(false);
  const [camposFaltantes, setCamposFaltantes] = useState([]);
  const [perfilModalContext, setPerfilModalContext] = useState("postular");
  const [perfilForm, setPerfilForm] = useState({ rut: "", pasaporte: "", fechaNacimiento: "", instagram: "", facebook: "", tiktok: "" });
  const [perfilFormErrors, setPerfilFormErrors] = useState({});
  const [perfilSaving, setPerfilSaving] = useState(false);
  const [pendingTicketAction, setPendingTicketAction] = useState(null);

  const navigate = useNavigate();

  // Track ticket quantities
  const [ticketQuantities, setTicketQuantities] = useState({});

  // Get cart context
  const { getCartForEvent, reloadCarts, carts } = useCart();

  console.log("carts:", carts);

  const updateTicketQuantity = (ticketId, change) => {
    setTicketQuantities((prev) => {
      const currentQuantity = prev[ticketId] || 0;
      const newQuantity = Math.max(0, currentQuantity + change);

      // Find the ticket to check limits
      const ticket = evento.tickets.find((t) => t.id === ticketId);
      const maxAllowed = ticket?.limitePorPersona || 10;

      return {
        ...prev,
        [ticketId]: Math.min(newQuantity, maxAllowed),
      };
    });
  };

  // Helper functions that depend on state - use useCallback to prevent recreation
  const getSelectedTicketsForCart = useCallback(() => {
    if (!evento?.tickets) return [];

    console.log(
      "getSelectedTicketsForCart - ticketQuantities:",
      ticketQuantities,
    );
    console.log(
      "Calculating selected tickets for cart with quantities:",
      evento?.tickets,
    );
    return evento.tickets
      .filter((ticket) => !ticket.esInvitacion)
      .map((ticket) => ({
        idTipoTicket: ticket.id,
        cantidad: ticketQuantities[ticket.id] || 0,
        precioVenta: ticket.precio,
      }))
      .filter((ticket) => ticket.cantidad > 0);
  }, [ticketQuantities, evento]);

  const getTotalSelectedTickets = useCallback(() => {
    const total = Object.values(ticketQuantities).reduce(
      (total, qty) => total + qty,
      0,
    );
    console.log(
      "getTotalSelectedTickets - ticketQuantities:",
      ticketQuantities,
      "total:",
      total,
    );
    return total;
  }, [ticketQuantities]);

  // Handle solicitar invitacion for a specific ticket
  const handleSolicitarInvitacion = useCallback(async (ticket) => {
    if (!isAuthenticated) {
      setAuthPromptOpen(true);
      return;
    }

    const ticketId = ticket.id;

    // Verificar fecha de nacimiento antes de postular
    const faltaFecha = infoRef.current !== null && !infoRef.current?.fechaNacimiento;
    const faltaDocumento = !tieneDocumento(userRef.current);
    if (faltaDocumento || faltaFecha) {
      const faltantes = [];
      if (faltaDocumento) faltantes.push("documento");
      if (faltaFecha) faltantes.push("fechaNacimiento");
      setCamposFaltantes(faltantes);
      setPerfilModalContext("postular");
      setPerfilForm({ rut: "", pasaporte: "", fechaNacimiento: "", instagram: urlToHandle(infoRef.current?.instagram, "instagram.com"), facebook: urlToHandle(infoRef.current?.facebook, "facebook.com"), tiktok: urlToHandle(infoRef.current?.tiktok, "tiktok.com") });
      setPendingTicketAction(() => () => handleSolicitarInvitacion(ticket));
      setShowPerfilModal(true);
      return;
    }

    setInvitacionState((prev) => ({
      ...prev,
      [ticketId]: { loading: true, error: null, solicitudCreada: prev[ticketId]?.solicitudCreada || null },
    }));

    try {
      const referralInfo = getReferralForEvent(evento.id);
      const ambassadorReferralCode = referralInfo?.code || null;
      const result = await dispatch(
        crearSolicitudInvitacion({
          uidEvento: evento.id,
          idTipoTicketInvitacion: ticketId,
          ambassadorReferralCode,
        })
      ).unwrap();
      setInvitacionState((prev) => ({
        ...prev,
        [ticketId]: { loading: false, error: null, solicitudCreada: result },
      }));
    } catch (err) {
      const status = err?.status || err?.statusCode;
      const esPerfilIncompleto =
        status === 422 ||
        err?.code === "PERFIL_INCOMPLETO" ||
        Array.isArray(err?.camposFaltantes);
      if (esPerfilIncompleto) {
        const faltantes = err?.camposFaltantes || [];
        if (!infoRef.current?.fechaNacimiento && !faltantes.includes("fechaNacimiento")) faltantes.push("fechaNacimiento");
        setCamposFaltantes(faltantes);
        setPerfilModalContext("postular");
        setPerfilForm({ rut: "", pasaporte: "", fechaNacimiento: "", instagram: urlToHandle(infoRef.current?.instagram, "instagram.com"), facebook: urlToHandle(infoRef.current?.facebook, "facebook.com"), tiktok: urlToHandle(infoRef.current?.tiktok, "tiktok.com") });
        setPendingTicketAction(() => () => handleSolicitarInvitacion(ticket));
        setShowPerfilModal(true);
        setInvitacionState((prev) => ({
          ...prev,
          [ticketId]: { loading: false, error: null, solicitudCreada: null },
        }));
      } else {
        let mensaje;
        if (status === 409 || (typeof err === "string" && err.includes("409"))) {
          mensaje = "Ya tienes una solicitud activa para este evento.";
        } else {
          mensaje =
            err?.message || (typeof err === "string" ? err : "Error al solicitar la invitacion");
        }
        setInvitacionState((prev) => ({
          ...prev,
          [ticketId]: { loading: false, error: mensaje, solicitudCreada: null },
        }));
      }
    }
  }, [isAuthenticated, dispatch, evento?.id]);

  // Create stable add to cart function using useCallback
  const handleAddToCart = useCallback(async () => {
    // Verificar documento y fecha de nacimiento antes de intentar agregar al carrito
    const faltaDocumento = !tieneDocumento(userRef.current);
    const faltaFecha = infoRef.current !== null && !infoRef.current?.fechaNacimiento;
    if (faltaDocumento || faltaFecha) {
      const faltantes = [];
      if (faltaDocumento) faltantes.push("documento");
      if (faltaFecha) faltantes.push("fechaNacimiento");
      setCamposFaltantes(faltantes);
      setPerfilModalContext("comprar");
      setPerfilForm({ rut: "", pasaporte: "", fechaNacimiento: "", instagram: urlToHandle(infoRef.current?.instagram, "instagram.com"), facebook: urlToHandle(infoRef.current?.facebook, "facebook.com"), tiktok: urlToHandle(infoRef.current?.tiktok, "tiktok.com") });
      setPendingTicketAction(() => handleAddToCart);
      setShowPerfilModal(true);
      return false;
    }

    const selectedTickets = getSelectedTicketsForCart();
    // Obtener código de referido si existe
    const referralInfo = getReferralForEvent(evento.id);
    // Verificar si hay código de referido
    const ambassadorReferralCode = referralInfo?.code || null;

    if (selectedTickets.length > 0) {
      try {
        console.log("id del evento", evento.id);
        // Verificar si ya existe un carrito para este evento
        const existingCart = getCartForEvent(evento.id);

        if (existingCart) {
          // Si existe un carrito, agregar tickets al carrito existente
          await dispatch(
            addTicketsFromEventToExistingCart({
              eventoUid: evento.id,
              carritoUid: existingCart.carritoUid,
              tickets: selectedTickets,
              ambassadorReferralCode,
            }),
          ).unwrap();
        } else {
          // Si no existe carrito, crear uno nuevo
          await dispatch(
            crearCarrito({
              eventoUid: evento.id,
              tickets: selectedTickets,
              ambassadorReferralCode,
            }),
          ).unwrap();
        }

        // Limpiar cantidades después de agregar al carrito
        console.log("Limpiando ticketQuantities...");
        setTicketQuantities({});
        console.log("ticketQuantities limpiado");

        // Recargar carritos
        reloadCarts();

        return true;
      } catch (error) {
        console.error("Error al agregar tickets al carrito:", error);

        if (error?.code === "PERFIL_INCOMPLETO" || error?.status === 422 || Array.isArray(error?.camposFaltantes)) {
          setCamposFaltantes(error?.camposFaltantes || []);
          setPerfilModalContext("comprar");
          setPerfilForm({ rut: "", pasaporte: "", instagram: urlToHandle(info?.instagram, "instagram.com"), facebook: urlToHandle(info?.facebook, "facebook.com"), tiktok: urlToHandle(info?.tiktok, "tiktok.com") });
          setPendingTicketAction(() => handleAddToCart);
          setShowPerfilModal(true);
          return false;
        }

        // Manejo específico para el error de auto-referido
        if (error?.code === "SELF_REFERRAL") {
          // Limpiar localStorage del referido para este evento. 'ambassador_referrals' es el key que se está usando en localStorage, debería eliminar el objeto en 'ambassador_referrals' de solo el referido de este evento, busca el objeto por el el valor 'code' que coincide con el código de referido del evento actual.
          const storedReferrals = localStorage.getItem("ambassador_referrals");

          console.log("SELF_REFERRAL 1:", storedReferrals);
          if (storedReferrals) {
            console.log("SELF_REFERRAL 2");
            // Parsear el objeto de referidos
            const referralsObj = JSON.parse(storedReferrals);
            console.log("SELF_REFERRAL 3:", referralsObj);

            // Obtener el referido para este evento
            const referralForEvent = getReferralForEvent(evento.id);
            console.log("SELF_REFERRAL 4:", referralForEvent);

            // Si existe, eliminarlo del objeto y guardar de nuevo
            if (referralForEvent) {
              console.log(
                "SELF_REFERRAL 5: Removing referral code",
                referralForEvent.uid,
              );
              // Eliminar el referido de este evento
              delete referralsObj[evento.id];
              // Guardar el objeto actualizado en localStorage
              localStorage.setItem(
                "ambassador_referrals",
                JSON.stringify(referralsObj),
              );
              console.log(
                "SELF_REFERRAL 6: Updated referrals in localStorage",
                referralsObj,
              );
            }
          }

          // Mostrar modal de error con mensaje específico
          setErrorReferidoMessage(
            "Llegaste a este evento o estás intentando comprar tickets con tu propio link de referido. Esto no está permitido. Se eliminará el código de referido para que puedas intentar nuevamente sin referido o con otro código de referido.",
          );
          setOpenModalErrorReferido(true);
        } else if (error?.code === "INVALID_REFERRAL") {
          setErrorReferidoMessage(
            "Llegaste a este evento con un código de referido no válido para este evento.",
          );
          setOpenModalErrorReferido(true);
        }

        return false;
      }
    }
    return false;
  }, [
    getSelectedTicketsForCart,
    evento,
    dispatch,
    reloadCarts,
    getCartForEvent,
  ]);

  const handleGuardarPerfil = async () => {
    const errors = {};

    const tipoDoc = user?.tipoDocumento || 'rut';
    if (tipoDoc === 'pasaporte') {
      if (!perfilForm.pasaporte) {
        errors.documento = "El número de pasaporte es obligatorio";
      } else if (!/^[A-Z0-9]{6,20}$/i.test(perfilForm.pasaporte)) {
        errors.documento = "Número de pasaporte inválido (6-20 caracteres alfanuméricos)";
      }
    } else if (!user?.numeroTributario) {
      if (!perfilForm.rut) {
        errors.documento = "El RUT es obligatorio";
      } else if (!validarRUT(perfilForm.rut)) {
        errors.documento = "RUT inválido";
      }
    }

    if (camposFaltantes.includes("fechaNacimiento")) {
      if (!perfilForm.fechaNacimiento) {
        errors.fechaNacimiento = "La fecha de nacimiento es obligatoria";
      } else {
        const hoy = new Date();
        const nacimiento = new Date(perfilForm.fechaNacimiento);
        const edad = hoy.getFullYear() - nacimiento.getFullYear() - (hoy < new Date(hoy.getFullYear(), nacimiento.getMonth(), nacimiento.getDate()) ? 1 : 0);
        if (edad < 13) errors.fechaNacimiento = "Debes tener al menos 13 años";
      }
    }

    if (perfilModalContext === "postular") {
      const hasSocial = validateSocialHandle(perfilForm.instagram) || validateSocialHandle(perfilForm.facebook) || validateSocialHandle(perfilForm.tiktok);
      if (!hasSocial) {
        errors.social = "Debes agregar al menos una red social";
      }
    }

    setPerfilFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setPerfilSaving(true);
    try {
      const payload = {};
      const tipoDoc = user?.tipoDocumento || 'rut';
      if (tipoDoc === 'pasaporte' && perfilForm.pasaporte) {
        payload.tipoDocumento = 'pasaporte';
        payload.numeroPasaporte = perfilForm.pasaporte;
      } else if (!user?.numeroTributario && perfilForm.rut) {
        payload.numeroTributario = perfilForm.rut;
      }
      if (camposFaltantes.includes("fechaNacimiento") && perfilForm.fechaNacimiento) payload.fechaNacimiento = perfilForm.fechaNacimiento;
      if (validateSocialHandle(perfilForm.instagram)) payload.instagram = handleToURL(perfilForm.instagram, "https://www.instagram.com/");
      if (validateSocialHandle(perfilForm.facebook)) payload.facebook = handleToURL(perfilForm.facebook, "https://www.facebook.com/");
      if (validateSocialHandle(perfilForm.tiktok)) payload.tiktok = handleToURL(perfilForm.tiktok, "https://www.tiktok.com/@", true);

      await dispatch(updateRedesPerfil(payload)).unwrap();
      setShowPerfilModal(false);
      if (pendingTicketAction) {
        await pendingTicketAction();
      }
    } catch (err) {
      setPerfilFormErrors({ general: err?.toString() || "Error al guardar" });
    } finally {
      setPerfilSaving(false);
    }
  };

  // When info loads after modal is already open, populate social fields
  useEffect(() => {
    if (showPerfilModal && info) {
      setPerfilForm((prev) => ({
        ...prev,
        fechaNacimiento: info.fechaNacimiento || prev.fechaNacimiento,
        instagram: urlToHandle(info.instagram, "instagram.com") || prev.instagram,
        facebook: urlToHandle(info.facebook, "facebook.com") || prev.facebook,
        tiktok: urlToHandle(info.tiktok, "tiktok.com") || prev.tiktok,
      }));
    }
  }, [info, showPerfilModal]);

  // Notify parent component about ticket changes
  useEffect(() => {
    if (
      evento?.onTicketsChange &&
      typeof evento.onTicketsChange === "function"
    ) {
      const hasTickets = getTotalSelectedTickets() > 0;
      const totalTickets = getTotalSelectedTickets();

      console.log(
        "Notifying parent - hasTickets:",
        hasTickets,
        "totalTickets:",
        totalTickets,
        "ticketQuantities:",
        ticketQuantities,
      );

      evento.onTicketsChange({
        hasTickets,
        totalTickets,
        addToCart: handleAddToCart,
      });
    }
  }, [getTotalSelectedTickets, evento?.onTicketsChange, ticketQuantities]); // Added ticketQuantities to dependencies

  return (
    <>
      <div className="bg-aw-bluedark-95 rounded-2xl sm:p-6 w-full flex flex-col min-h-fit xl:min-h-[80dvh]">
        <div className="text-white font-bold text-lg mb-4">
          Obtén Tu Ticket Aquí!
          <p className="text-yellow-500 italic font-semibold text-xs">
            Nota: Todos los tickets quedarán asociados a tu RUT
          </p>
        </div>
        {evento?.tickets?.map((ticket, i) => {
          // Format price display
          const formatPrice = (price) => {
            if (!price || price === 0) return "GRATIS";
            return new Intl.NumberFormat("es-CL", {
              style: "currency",
              currency: "CLP",
              minimumFractionDigits: 0,
            }).format(price);
          };

          // Get ticket image - use custom image if available and not using event image, otherwise use event image
          const getTicketImage = () => {
            if (ticket.useImgEvento === 0 && ticket.imgTicket) {
              return ticket.imgTicket;
            }
            return evento.img;
          };

          // Use the backend esInvitacion flag to determine invitation tickets
          const isInvitation = !!ticket.esInvitacion;

          // Invitation-specific derived state
          const invState = invitacionState[ticket.id] || {};
          const solicitudActiva = invState.solicitudCreada || miSolicitudEnEvento;
          const solicitudEsDeEsteTicket =
            !!invState.solicitudCreada || // creada localmente para este ticketId exacto
            // eslint-disable-next-line eqeqeq
            solicitudActiva?.idTipoTicketInvitacion == ticket.id ||
            // eslint-disable-next-line eqeqeq
            solicitudActiva?.tipoTicketId == ticket.id ||
            (isInvitation && solicitudActiva && !invState.solicitudCreada);

          return (
            <div
              key={i}
              className="flex items-center gap-3 bg-aw-bluedark rounded-xl p-3 mb-3 border border-aw-green relative"
            >
              <img
                src={getTicketImage()}
                alt={ticket.customTypeName}
                className="w-16 h-16 rounded-lg object-cover"
              />
              <div className="flex-1">
                <p className="text-white font-bold text-sm">
                  Ticket
                  <span className="block">{ticket.customTypeName}</span>
                </p>
                <div className="text-aw-green font-bold text-lg">
                  <span className="inline-block mr-1">
                    {isInvitation ? "Solo Invitación" : formatPrice(ticket.precio)}
                  </span>
                  <span className="text-gray-400 font-normal text-xs block sm:inline-block">
                    {ticket.ticketGrupal
                      ? `para ${ticket.cantidadPersonasTicketGrupal} personas`
                      : "/ por persona"}
                  </span>
                </div>
              </div>
              {/* Action area — only visible while the event hasn't ended */}
              {new Date(evento.fechaHoraFin) > new Date() && (
                <div className="flex flex-col gap-1 items-end mt-1">
                  {isInvitation ? (
                    // ----- INVITATION TICKET ACTIONS -----
                    (() => {
                      // Show existing solicitud state
                      if (solicitudEsDeEsteTicket && solicitudActiva && solicitudActiva.estado !== "rechazada") {
                        const colores = getColorByEstadoInvitacion(solicitudActiva.estado);
                        return (
                          <div className="flex flex-col items-end gap-1">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colores.badge}`}>
                              {colores.label}
                            </span>
                            {solicitudActiva.uid && user?.uid && (
                              <Link
                                to={`/perfil/${user.uid}/invitaciones/${solicitudActiva.uid}`}
                                className="text-xs text-aw-green underline"
                              >
                                Ver solicitud
                              </Link>
                            )}
                          </div>
                        );
                      }

                      // Show error if any
                      if (invState.error) {
                        return (
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-xs text-red-400 text-right max-w-[140px]">
                              {invState.error}
                            </span>
                            <button
                              onClick={() => handleSolicitarInvitacion(ticket)}
                              className="border border-aw-green text-aw-green px-3 py-1 rounded-full text-xs font-bold hover:bg-aw-green hover:text-aw-bluedark transition"
                            >
                              Reintentar
                            </button>
                          </div>
                        );
                      }

                      // Show solicitar button
                      return (
                        <button
                          onClick={() => handleSolicitarInvitacion(ticket)}
                          disabled={invState.loading}
                          className={`border border-aw-green text-aw-green px-3 py-1 rounded-full text-xs font-bold transition flex items-center gap-1 ${
                            invState.loading
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-aw-green hover:text-aw-bluedark"
                          }`}
                        >
                          {invState.loading ? (
                            <>
                              <FontAwesomeIcon icon="fa-solid fa-spinner" className="animate-spin" />
                              Enviando...
                            </>
                          ) : (
                            "Postularme"
                          )}
                        </button>
                      );
                    })()
                  ) : carts.some((cart) =>
                      cart?.tickets.some(
                        (tckt) => tckt.tipoTicketId == ticket.id,
                      ),
                    ) ? (
                    <Tooltip
                      content="Ya has agregado este ticket al carrito o ya has comprado el límite permitido por persona"
                      position={`${
                        window.innerWidth < 640 ? "center_xs" : "center"
                      }`}
                    >
                      <div className="flex items-center gap-2 opacity-50 cursor-not-allowed bg-aw-green-30 rounded-2xl px-2 py-1">
                        <button
                          disabled
                          className="w-7 h-7 rounded-full bg-aw-bluedark text-aw-green border border-aw-green flex items-center justify-center font-bold text-lg opacity-50 cursor-not-allowed"
                        >
                          -
                        </button>
                        <span className="text-white font-bold min-w-[2rem] text-center">
                          {ticketQuantities[ticket.id] || 0}
                        </span>
                        <button
                          disabled
                          className="w-7 h-7 rounded-full bg-aw-bluedark text-aw-green border border-aw-green flex items-center justify-center font-bold text-lg opacity-50 cursor-not-allowed"
                        >
                          +
                        </button>
                      </div>
                    </Tooltip>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          if (!isAuthenticated) {
                            setAuthPromptOpen(true);
                          } else {
                            updateTicketQuantity(ticket.id, -1);
                          }
                        }}
                        disabled={(ticketQuantities[ticket.id] || 0) === 0}
                        className={`w-7 h-7 rounded-full bg-aw-bluedark text-aw-green border border-aw-green flex items-center justify-center font-bold text-lg transition ${
                          (ticketQuantities[ticket.id] || 0) === 0
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-aw-green hover:text-aw-bluedark"
                        }`}
                      >
                        -
                      </button>
                      <span className="text-white font-bold min-w-[2rem] text-center">
                        {ticketQuantities[ticket.id] || 0}
                      </span>
                      <button
                        onClick={() => {
                          if (!isAuthenticated) {
                            setAuthPromptOpen(true);
                          } else {
                            updateTicketQuantity(ticket.id, 1);
                          }
                        }}
                        disabled={
                          (ticketQuantities[ticket.id] || 0) >=
                          (ticket.limitePorPersona || 10)
                        }
                        className={`w-7 h-7 rounded-full bg-aw-bluedark text-aw-green border border-aw-green flex items-center justify-center font-bold text-lg transition ${
                          (ticketQuantities[ticket.id] || 0) >=
                          (ticket.limitePorPersona || 10)
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-aw-green hover:text-aw-bluedark"
                        }`}
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Modals - outside the map */}
        <Modal
          isOpen={openModalActualizarYPostularATicketGratis}
          onClose={() => setOpenModalActualizarYPostularATicketGratis(false)}
          large="max-w-4xl"
        >
          <ActualizarPerfilYPostularATicketGratis
            LogoEmpresa={LogoPerfilEmpresa}
            openPoliticasYPostular={() => {
              setOpenModalActualizarYPostularATicketGratis(false);
              setTimeout(() => {
                setOpenModalPoliticasYPostular(true);
              }, 300);
            }}
          />
        </Modal>
        <Modal
          isOpen={openModalPoliticasYPostular}
          onClose={() => setOpenModalPoliticasYPostular(false)}
          large="max-w-4xl"
        >
          <PoliticasYPostularATicketGratis
            LogoEmpresa={LogoPerfilEmpresa}
            openSolicitudEnviada={() => {
              setOpenModalPoliticasYPostular(false);
              setTimeout(() => {
                setAvisoSolicitudEnviada(true);
              }, 300);
            }}
          />
        </Modal>

        <Modal
          isOpen={avisoSolicitudEnviada}
          onClose={() => setAvisoSolicitudEnviada(false)}
          large="max-w-xl"
        >
          <div className="p-6 text-center bg-aw-bluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green">
            <div className="text-center">
              <FontAwesomeIcon
                icon={"fa-solid fa-circle-check"}
                className="text-aw-green text-6xl mb-4 mx-auto"
              />
              <h2 className="text-2xl font-semibold mb-2 text-white">
                Postulación Enviada
              </h2>
              <p className="text-gray-400 mb-4">
                ¡Gracias por postular al ticket gratis! Hemos recibido tu
                solicitud y nuestro equipo la está revisando.
              </p>
              {/* TODO: Agregar la información condicional según el exito o rechazo de la solicitud y colocar los CTA que se requieran */}
            </div>
          </div>
        </Modal>

        {/* Modal: perfil incompleto */}
        <Modal
          isOpen={showPerfilModal}
          onClose={() => setShowPerfilModal(false)}
          large="max-w-md"
        >
          <div className="p-6 bg-aw-bgbluedark rounded-lg text-center">
            <FontAwesomeIcon icon="fa-solid fa-user-circle" className="text-5xl text-aw-green mb-3 mx-auto" />
            <h3 className="text-lg font-bold text-white mb-1">Completa tu perfil</h3>
            <p className="text-sm text-gray-400 mb-4">
              {perfilModalContext === "postular"
                ? "Para postularte necesitas tu documento de identidad (RUT o pasaporte) y al menos una red social."
                : camposFaltantes.includes("fechaNacimiento") && camposFaltantes.includes("documento")
                  ? `Para comprar tickets necesitas tu ${(user?.tipoDocumento || 'rut') === 'pasaporte' ? 'pasaporte' : 'RUT'} y tu fecha de nacimiento.`
                  : camposFaltantes.includes("fechaNacimiento")
                    ? "Para comprar tickets necesitas ingresar tu fecha de nacimiento."
                    : `Para comprar tickets necesitas tu ${(user?.tipoDocumento || 'rut') === 'pasaporte' ? 'pasaporte' : 'RUT'}.`}
            </p>

            {!tieneDocumento(user) && (
              <div className="mb-3 text-left">
                <label className="block text-xs font-semibold text-gray-300 mb-1">
                  {(user?.tipoDocumento || 'rut') === 'pasaporte' ? 'Número de Pasaporte' : 'RUT'}
                  <span className="text-red-400 ml-1">*</span>
                  {(user?.tipoDocumento || 'rut') === 'rut' && (
                    <span className="ml-2 text-amber-400 text-[10px] font-normal">No editable después de guardarlo</span>
                  )}
                </label>
                {(user?.tipoDocumento || 'rut') === 'pasaporte' ? (
                  <input
                    type="text"
                    value={perfilForm.pasaporte || ''}
                    onChange={(e) => setPerfilForm((p) => ({ ...p, pasaporte: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                    placeholder="ej: AB1234567"
                    maxLength={20}
                    className="w-full px-3 py-2 bg-aw-bluedark border border-gray-600 rounded-xl text-white text-sm focus:outline-none focus:border-aw-green"
                  />
                ) : (
                  <input
                    type="text"
                    value={perfilForm.rut}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9Kk]/g, "");
                      if (raw.length <= 10) setPerfilForm((p) => ({ ...p, rut: formatearRUT(raw) }));
                    }}
                    placeholder="12.345.678-9"
                    className="w-full px-3 py-2 bg-aw-bluedark border border-gray-600 rounded-xl text-white text-sm focus:outline-none focus:border-aw-green"
                  />
                )}
                {(perfilFormErrors.documento || perfilFormErrors.rut) && (
                  <p className="text-red-400 text-xs mt-1">{perfilFormErrors.documento || perfilFormErrors.rut}</p>
                )}
              </div>
            )}

            {camposFaltantes.includes("fechaNacimiento") && (
              <div className="mb-3 text-left">
                <label className="block text-xs font-semibold text-gray-300 mb-1">
                  Fecha de nacimiento <span className="text-red-400">*</span>
                </label>
                <input
                  type="date"
                  value={perfilForm.fechaNacimiento}
                  max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 13); return d.toISOString().split("T")[0]; })()}
                  onChange={(e) => setPerfilForm((p) => ({ ...p, fechaNacimiento: e.target.value }))}
                  className="w-full px-3 py-2 bg-aw-bluedark border border-gray-600 rounded-xl text-white text-sm focus:outline-none focus:border-aw-green"
                />
                {perfilFormErrors.fechaNacimiento && <p className="text-red-400 text-xs mt-1">{perfilFormErrors.fechaNacimiento}</p>}
              </div>
            )}

            <div className="mb-4 text-left space-y-2">
              <label className="block text-xs font-semibold text-gray-300 mb-1">
                Redes Sociales
                {perfilModalContext === "postular"
                  ? <span className="text-red-400 ml-1">* (al menos una)</span>
                  : <span className="text-gray-500 ml-1">(opcional)</span>}
              </label>
              {[
                { key: "instagram", icon: "fa-brands fa-instagram", iconClass: "text-pink-400", placeholder: "@tu_usuario" },
                { key: "facebook",  icon: "fa-brands fa-facebook",  iconClass: "text-blue-400", placeholder: "@tu_usuario" },
                { key: "tiktok",    icon: "fa-brands fa-tiktok",    iconClass: "text-white",    placeholder: "@tu_usuario" },
              ].map(({ key, icon, iconClass, placeholder }) => (
                <div key={key} className="flex items-center gap-2">
                  <FontAwesomeIcon icon={icon} className={`${iconClass} w-5 shrink-0`} />
                  <input
                    type="text"
                    value={perfilForm[key]}
                    onChange={(e) => {
                      let v = e.target.value;
                      if (v && !v.startsWith("@")) v = "@" + v.replace(/^@+/, "");
                      setPerfilForm((p) => ({ ...p, [key]: v }));
                    }}
                    placeholder={placeholder}
                    className="flex-1 px-3 py-2 bg-aw-bluedark border border-gray-600 rounded-xl text-white text-sm focus:outline-none focus:border-aw-green"
                  />
                </div>
              ))}
              {perfilFormErrors.social && <p className="text-red-400 text-xs mt-1">{perfilFormErrors.social}</p>}
            </div>

            {perfilFormErrors.general && (
              <p className="text-red-400 text-xs mb-3">{perfilFormErrors.general}</p>
            )}

            <button
              onClick={handleGuardarPerfil}
              disabled={perfilSaving}
              className="w-full py-2.5 bg-aw-green text-aw-bluedark rounded-xl font-bold text-sm hover:scale-[1.02] transition disabled:opacity-50"
            >
              {perfilSaving ? "Guardando..." : "Guardar y continuar"}
            </button>
          </div>
        </Modal>

        <Modal
          isOpen={openModalErrorReferido}
          onClose={() => setOpenModalErrorReferido(false)}
          large="max-w-xl"
        >
          <div className="p-6 text-center bg-aw-bluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green">
            <div className="text-center">
              <FontAwesomeIcon
                icon={"fa-solid fa-circle-exclamation"}
                className="text-red-500 text-6xl mb-4 mx-auto"
              />
              <h2 className="text-2xl font-semibold mb-2 text-white">
                Error con Código de Referido
              </h2>
              <p className="text-gray-300 mb-6">{errorReferidoMessage}</p>
              <button
                onClick={() => setOpenModalErrorReferido(false)}
                className="bg-aw-green text-aw-bluedark px-6 py-2 rounded-full font-bold hover:bg-aw-green-70 transition"
              >
                Entendido
              </button>
            </div>
          </div>
        </Modal>
      </div>

      <AuthPromptModal
        isOpen={authPromptOpen}
        onClose={() => setAuthPromptOpen(false)}
        returnTo={evento?.id ? `/evento/${evento.id}` : undefined}
      />
    </>
  );
}
